import { ApolloClient, InMemoryCache, from } from '@apollo/client'
import { createUploadLink } from 'apollo-upload-client'
import { onError } from '@apollo/client/link/error'
import { RetryLink } from '@apollo/client/link/retry'

import * as Sentry from '@sentry/nextjs'

const errorLink = onError(
  ({ graphQLErrors, networkError, operation, response }) => {
    if (graphQLErrors) {
      graphQLErrors.forEach(({ message, locations, path }) => {
        const errorMessage = `[GraphQL error]: Message: ${message}, Operation: ${operation?.operationName}`
        Sentry.captureException(new Error(errorMessage), {
          tags: {
            graphql: 'error'
          },
          extra: {
            message,
            locations,
            path,
            operation,
            response
          }
        })
      })
    }
    if (networkError) {
      const errorMessage = `[Network error]: ${networkError}, Operation: ${operation?.operationName}`
      Sentry.captureException(new Error(errorMessage), {
        tags: {
          graphql: 'network'
        },
        extra: {
          networkError,
          operation,
          response
        }
      })
    }
  }
)

const retryLink = new RetryLink({
  delay: {
    initial: 300,
    max: Infinity,
    jitter: true
  },
  attempts: {
    max: 3,
    retryIf: (error, _operation) => !!error
  }
})

export const createApolloClient = (locale = 'es_AR') => {
  const isSsr = typeof window === 'undefined'
  const country = locale.split('_')[1].toLowerCase()

  const httpLink = createUploadLink({
    uri: `${process.env.NEXT_PUBLIC_API_ENDPOINT}${country}/graphql`,
    headers: {
      'X-WEB-TOKEN': process.env.NICOX_API_X_WEB_TOKEN
    }
  })

  return new ApolloClient({
    ssrMode: isSsr,
    link: from([retryLink, errorLink, httpLink]),
    cache: new InMemoryCache()
  })
}

export const hidrateApolloClient = ({ locale = 'es_AR', authToken }) => {
  const country = locale.split('_')[1].toLowerCase()
  const httpLink = createUploadLink({
    uri: `${process.env.NEXT_PUBLIC_API_ENDPOINT}${country}/graphql`,
    headers: {
      authorization: authToken ? `Bearer ${authToken}` : '',
      'X-WEB-TOKEN': process.env.NICOX_API_X_WEB_TOKEN
    }
  })
  return new ApolloClient({
    link: from([retryLink, errorLink, httpLink]),
    cache: new InMemoryCache()
  })
}

export const createApolloClientWithToken = (locale = 'es_AR', token) => {
  const country = locale.split('_')[1].toLowerCase()

  const httpLink = createUploadLink({
    uri: `${process.env.NEXT_PUBLIC_API_ENDPOINT}${country}/graphql`,
    headers: {
      authorization: token ? `Bearer ${token}` : '',
      'X-WEB-TOKEN': process.env.NICOX_API_X_WEB_TOKEN
    }
  })
  return new ApolloClient({
    link: from([retryLink, errorLink, httpLink]),
    cache: new InMemoryCache()
  })
}
